import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from "react";

import { Metric, ObjectType } from "../../../../../models";
import { usePublicContext } from "../../../../../route/public";
import { register } from "../../../../../service/identity";
import { registerProjectInterest } from "../../../../../service/public";
import { ServiceError, Status } from "../../../../../service/Shared";
import { saveEvent } from "../../../../../service/stats";
import { useIsLoadingWrapper } from "../../../../../utils";
import { Toast } from "../../../../../widget";

interface UseRegisterInterestModalFormProps {
  closeModal: () => void;
}

interface UseRegisterInterestModalReturnData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  password: string;
  errors: ServiceError[] | undefined;
  displaySecondStepSuccessBox: boolean;
  firstStepComplete: boolean;
  secondStepComplete: boolean;
  isHandleFirstStepSubmitLoading: boolean;
  recaptchaToken: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  setLastName: Dispatch<SetStateAction<string>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setMessage: Dispatch<SetStateAction<string>>;
  setPassword: Dispatch<SetStateAction<string>>;
  handleFirstStepSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleSecondStepSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setRecaptchaToken: Dispatch<SetStateAction<string>>;
}

export const useRegisterInterestModalForm = ({
  closeModal,
}: UseRegisterInterestModalFormProps): UseRegisterInterestModalReturnData => {
  const { projectDetails } = usePublicContext();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<ServiceError[] | undefined>([]);

  const [displaySecondStepSuccessBox, setDisplaySecondStepSuccessBox] = useState(true);
  const [firstStepComplete, setFirstStepComplete] = useState(false);
  const [secondStepComplete, setSecondStepComplete] = useState(false);
  const [isHandleFirstStepSubmitLoading, setIsHandleFirstStepSubmitLoading] = useState(false);

  const [recaptchaToken, setRecaptchaToken] = useState("");

  const handleFirstStepSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (projectDetails) {
      const res = await registerProjectInterest({
        projectUuid: projectDetails?.uuid,
        firstName,
        lastName,
        email,
        message,
        clientCaptchaResponseToken: recaptchaToken,
      });

      if (res.status === Status.Success) {
        Toast.success({ message: "Thanks for getting in touch." });
        closeModal();
        setFirstStepComplete(true);
        saveEvent({
          metric: Metric.Enquiry,
          objectType: ObjectType.Project,
          objectUuid: projectDetails?.uuid,
          timestamp: null,
        });
      }
      if (res.status === Status.Error) {
        Toast.error({ message: "There was an error sending your message." });
        setErrors(res.errors);
      }
    }
  }, setIsHandleFirstStepSubmitLoading);

  const handleSecondStepSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const res = await register({
      firstName,
      lastName,
      email,
      password,
    });

    if (res.status === Status.Success) {
      setSecondStepComplete(true);
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
      setDisplaySecondStepSuccessBox(false);
    }
  };

  useEffect(() => {
    document.getElementById("registerInterestSecondStep")?.scrollIntoView({ behavior: "smooth" });

    document.getElementById("registerInterestThirdStep")?.scrollIntoView({ behavior: "smooth" });
  }, [firstStepComplete, secondStepComplete]);

  return {
    firstName,
    lastName,
    email,
    message,
    password,
    errors,
    displaySecondStepSuccessBox,
    firstStepComplete,
    secondStepComplete,
    isHandleFirstStepSubmitLoading,
    recaptchaToken,
    setFirstName,
    setLastName,
    setEmail,
    setMessage,
    setPassword,
    handleFirstStepSubmit,
    handleSecondStepSubmit,
    setRecaptchaToken,
  };
};
